import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { FileSpreadsheet, History, LayoutDashboard, LogOut, Menu, MessageSquareWarning, PhoneCall, RefreshCw, Settings, KeyRound, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { BRAND_THEME } from "@/lib/constants";
import api, { errMsg } from "@/lib/api";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";

const NAV = [
  ["Dashboard", "/", LayoutDashboard, null],
  ["Complaints", "/complaints", MessageSquareWarning, null],
  ["Follow-ups", "/followups", PhoneCall, null],
  ["Upload Data", "/upload", FileSpreadsheet, ["admin", "manager"]],
  ["Upload History", "/history", History, null],
  ["Direct Complaints Sync", "/sync", RefreshCw, null],
  ["Settings", "/settings", Settings, ["admin"]],
];

const SideNav = ({ onNavigate }) => {
  const { user } = useAuth();
  const { activeBrand } = useApp();
  const t = BRAND_THEME[activeBrand];
  return (
    <div className="flex h-full flex-col">
      <div className="px-6 py-6">
        <div className="mb-3"><BrandLogo className="max-h-12 max-w-[170px]" testid="sidebar-logo" /></div>
        <div className="font-display text-xl font-bold tracking-tight text-slate-900">Feedback Hub</div>
        <div className="text-xs font-medium uppercase tracking-wider text-slate-400">Customer care</div>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.filter(([, , , roles]) => !roles || roles.includes(user.role)).map(([label, to, Icon]) => (
          <NavLink
            key={to} to={to} end={to === "/"} onClick={onNavigate}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-3 text-[15px] font-medium transition-colors ${isActive ? "" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}
            style={({ isActive }) => (isActive ? { background: t.bg, color: t.text } : undefined)}
          >
            <Icon className="h-5 w-5" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="m-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-500">Need help? See Settings → Help, or ask your admin.</div>
    </div>
  );
};

const BrandSwitcher = () => {
  const { meta, activeBrand, setBrand } = useApp();
  const onDashboard = useLocation().pathname === "/";
  if (!meta) return null;
  const list = meta.brands.length > 1 && !onDashboard ? ["All", ...meta.brands] : meta.brands;
  return (
    <div data-testid="brand-switcher" className="no-scrollbar flex items-center gap-1.5 overflow-x-auto">
      {list.map((b) => {
        const t = BRAND_THEME[b];
        const on = activeBrand === b || meta.brands.length === 1;
        return (
          <button
            key={b} onClick={() => setBrand(b)} data-testid={`brand-switch-${b.replace(/[^a-z]/gi, "").toLowerCase()}`}
            className="flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors"
            style={on ? { background: t.primary, color: "#fff", borderColor: t.primary } : { background: "#fff", color: "#475569", borderColor: "#E2E8F0" }}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: on ? "#fff" : t.primary }} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
};

const PasswordDialog = ({ open, onOpenChange }) => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const save = async () => {
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      onOpenChange(false);
      setCur(""); setNext("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Change your password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="current-password-input" />
          <Input type="password" placeholder="New password (min 6 characters)" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password-input" />
          <Button className="w-full" onClick={save} data-testid="change-password-submit">Save new password</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UserMenu = () => {
  const { user, logout } = useAuth();
  const [pw, setPw] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu-button" className="flex shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-3 hover:bg-slate-50">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">{user.name?.[0] || "U"}</span>
            <span className="hidden text-sm font-medium text-slate-700 md:block">{user.name}</span>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="text-sm">{user.email}</div>
            <div className="text-xs font-normal capitalize text-slate-500">{user.role}{user.brands?.length ? ` · ${user.brands.join(", ")}` : ""}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setPw(true)} data-testid="change-password-menu"><KeyRound className="mr-2 h-4 w-4" />Change password</DropdownMenuItem>
          <DropdownMenuItem onClick={logout} data-testid="logout-button"><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PasswordDialog open={pw} onOpenChange={setPw} />
    </>
  );
};

export default function Layout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-slate-200 bg-white lg:block"><SideNav /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0"><SheetTitle className="sr-only">Menu</SheetTitle><SideNav onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md sm:px-6">
          <button className="rounded-lg p-2 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1"><BrandSwitcher /></div>
          <UserMenu />
        </header>
        <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-6 lg:px-10"><Outlet /></main>
      </div>
    </div>
  );
}
